"use client";

import { useEffect, useRef, useState } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

type CcSelectedWorkVideoProps = {
  src: string;
  label: string;
};

/**
 * Muted looping client video over the tile's still image. Nothing downloads
 * until the tile nears the screen, it only plays while visible, and it stays
 * transparent until it actually plays, so the still shows whenever playback
 * is blocked; reduced motion keeps the still.
 */
export function CcSelectedWorkVideo({ src, label }: CcSelectedWorkVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const reducedMotion = useCcPrefersReducedMotion();
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reducedMotion) return;
    // React doesn't render `muted` as an attribute, and browsers only autoplay muted video.
    video.muted = true;
    video.defaultMuted = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (video.preload !== "auto") video.preload = "auto";
          void video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <video
      ref={videoRef}
      src={src}
      aria-label={label}
      data-playing={playing ? "true" : "false"}
      onPlaying={() => setPlaying(true)}
      muted
      loop
      playsInline
      preload="none"
    />
  );
}
